import { Divider, Dropdown, Tabs, Widget } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { forwardRef, ReactElement, useEffect, useState } from 'react';
import useFetchRiskProducts from '../../../hooks/riskProducts/useFetchRiskProducts';
import { Chart } from '../../../models/RiskProducts';
import { useCompanyRiskProductsContext } from '../../../pages/Company/CompanyRiskProductsContext';
import { getCompanyId } from '../../../utils/tagUtils';
import CqsChart from './CqsChart/CqsChart';
import { hasCqsMetrics } from './CqsChart/CqsChart.logic';
import CreChart from './CreChart/CreChart';
import { hasCreMetrics } from './CreChart/CreChart.logic';
import ErrorBoundary from '../../ErrorBoundary';
import { hasCompanyRiskMetrics, riskProductsWebsiteLinkGet } from './RiskProductsWidget.logic';
import styles from './RiskProductsWidget.module.scss';
import { TimeFrameOptions, RiskProductsProps, riskProductsDropdownOptions } from './RiskProductsWidget.types';

const cx = classNames.bind(styles);

const RiskProductsWidget = (
  {
    companyTag,
    dynamicHeader = false,
    rpMessage = false,
    noPrint,
    isTeaser = false,
    defaultTimeframe,
    ...sortableWidgetProps
  }: RiskProductsProps,
  ref
): ReactElement | null => {
  const [timeFrame, setTimeFrame] = useState<TimeFrameOptions>('3y');
  const companyId = getCompanyId(companyTag);
  // Node: We need to pass "enabled:false" to the hook useFetchRiskProducts, because the data is already fetched and cached by parent components. We just need to get data here. We don't want to pass it over params.
  // Enabling react-query hook in a lower component is causing infinitive components re-renders
  const {
    data: riskMetrics,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useFetchRiskProducts(companyId, {}, timeFrame, isTeaser);

  const { cqsMetrics, creMetrics, modelId, regionId } = riskMetrics;
  const { activeTab, setActiveTab } = useCompanyRiskProductsContext();
  const cqsMetricsExist = hasCqsMetrics(cqsMetrics);
  const creMetricsExist = hasCreMetrics(creMetrics);
  const companyRiskMetricsExists = hasCompanyRiskMetrics(cqsMetrics, creMetrics);

  const tabData = [
    ...(cqsMetricsExist
      ? [
          {
            active: activeTab === Chart.Cqs,
            onClick: () => setActiveTab(Chart.Cqs),
            label: 'CQS',
            dataTestId: 'cqs-chart-tab',
          },
        ]
      : []),
    ...(creMetricsExist
      ? [
          {
            active: activeTab === Chart.Cre,
            onClick: () => setActiveTab(Chart.Cre),
            label: 'CRE',
            dataTestId: 'cre-chart-tab',
          },
        ]
      : []),
  ];

  useEffect(() => {
    setTimeFrame(defaultTimeframe ?? '3y');
  }, [defaultTimeframe]);

  useEffect(() => {
    if (activeTab !== null) {
      return;
    }
    setActiveTab(Chart.Cqs);
  }, []);

  const isTabShown = (label: string): boolean => tabData.filter((tab) => tab.label === label).length > 0;

  const renderChart = () => {
    if (isTabShown('CQS') && activeTab === Chart.Cqs) {
      return <CqsChart cqsMetrics={cqsMetrics} timeframe={defaultTimeframe ? timeFrame : undefined} />;
    }
    if (isTabShown('CRE') && activeTab === Chart.Cre) {
      return (
        <CreChart
          modelId={modelId}
          regionId={regionId}
          creMetrics={creMetrics}
          timeframe={defaultTimeframe ? timeFrame : undefined}
        />
      );
    }
    return null;
  };

  const renderHeaderTitle = () => {
    if (!dynamicHeader) {
      return 'Risk Products';
    }
    return activeTab === Chart.Cqs ? 'CQS History' : 'CRE History';
  };

  if (!companyRiskMetricsExists && !isError && !isFetching) {
    return null;
  }

  return (
    <Widget
      classNames={cx({ 'no-print': noPrint })}
      headerTitle={renderHeaderTitle()}
      firstLine={
        <div className={styles.header}>
          <Tabs tabs={tabData} />
          {!isTeaser && (
            <Dropdown
              className={cx('dropdown')}
              value={timeFrame}
              options={riskProductsDropdownOptions as any}
              onOptionSelect={(value) => {
                if (typeof value === 'string') {
                  setTimeFrame(value as TimeFrameOptions);
                }
              }}
              withCaretIcon
              withCheckmarks
            />
          )}
        </div>
      }
      dataTestId="risk-products"
      ref={ref}
      fixedHeight
      isLoading={isLoading || isFetching}
      isError={isError}
      onRetryAfterErrorClick={refetch}
      {...sortableWidgetProps}
    >
      <div className={cx('body')} data-testid="risk-products-chart-wrapper">
        <ErrorBoundary onRetryClick={() => {}}>
          <>
            {!isFetching && renderChart()}
            <div className={cx('divider-wrapper')}>
              <Divider />
            </div>
            <div className={classNames(cx('description'), 'to-translate')}>
              {rpMessage && (
                <p>
                  CreditSights Risk Products is a suite of quantitative credit risk models designed to help you monitor
                  and manage risk across your portfolios. To access the full set of risk metrics across our entire
                  covered universe of more than 30,000 companies.{' '}
                  <a
                    href={riskProductsWebsiteLinkGet()}
                    className={cx('link')}
                    rel="noopener noreferrer"
                    target="_blank"
                    aria-label="Read more about full set of risk metrics"
                    data-testid="risk-products-source-link"
                  >
                    click here
                  </a>
                  .
                </p>
              )}
            </div>
          </>
        </ErrorBoundary>
      </div>
    </Widget>
  );
};

export default forwardRef(RiskProductsWidget);
